import { useEffect, useMemo, useRef, useState } from "react";
import type { RichChallengeProps } from "./richChallenges.ts";

// Themeable rich renderer for challenge_type === "terminal".
//
// One component, many faces: each challenge supplies its own look + content via
// the server-authored challenge_payload (accent color, header, tag, optional
// ASCII art, streamed boot narration, prompt). The flow is the SAME as every
// other challenge — onSubmit -> server validation -> on success the
// challenge_response clue is shown (that clue is what leads the player toward
// /obd2 and the U1984 code).
interface Payload {
  accent?: string; // theme color, e.g. "#67b0ff"
  header?: string; // top-left label
  tag?: string; // top-right status label
  art?: string; // optional monospace art block
  lines?: string[]; // boot narration, streamed in one at a time
  prompt?: string; // question above the input
  placeholder?: string;
}

// challenge_text lines first, then the payload boot lines; drop exact repeats
// so content that appears in both places doesn't print twice.
function dedupeConcat(a: string[] | undefined, b: string[]): string[] {
  const seen = new Set<string>();
  return [...(a || []), ...b].filter((l) => {
    if (seen.has(l)) return false;
    seen.add(l);
    return true;
  });
}

export function TerminalChallenge(props: RichChallengeProps) {
  const { payload, response, text, onSubmit, onClose } = props;
  const data = useMemo<Payload>(() => {
    try {
      return JSON.parse(payload || "{}");
    } catch {
      return {};
    }
  }, [payload]);

  const accent = data.accent || "#1FA8E0";

  // The displayed body is the human-editable `challenge_text` (the sheet
  // column) FOLLOWED BY the payload's boot narration (lines) — both stream in.
  // challenge_text carries the editable puzzle statement; the payload lines
  // add the terminal flavor. Exact-duplicate lines are dropped so content
  // that appears in both places doesn't print twice.
  const lines = useMemo<string[]>(() => {
    let textLines: string[] = [];
    const t = (text || "").trim();
    if (t) {
      const byNewline = t.split(/\n+/).map((s) => s.trim()).filter(Boolean);
      if (byNewline.length > 1) {
        textLines = byNewline;
      } else {
        // one paragraph -> split after sentence enders (no lookbehind, for Safari)
        textLines = t
          .replace(/([.!?])\s+/g, "$1\u0000")
          .split("\u0000")
          .map((s) => s.trim())
          .filter(Boolean);
      }
    }
    return dedupeConcat(textLines, data.lines || []);
  }, [text, data.lines]);

  // Stream the narration lines for a "booting" feel.
  const [revealed, setRevealed] = useState(0);
  useEffect(() => {
    setRevealed(0);
    const t = setInterval(
      () => setRevealed((n) => (n >= lines.length ? n : n + 1)),
      600,
    );
    return () => clearInterval(t);
  }, [payload]); // eslint-disable-line react-hooks/exhaustive-deps

  // keep the streaming feed pinned to the newest line (it scrolls internally
  // now — the card itself is fixed-size so the input/close never move)
  const feedRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (feedRef.current) {
      feedRef.current.scrollTop = feedRef.current.scrollHeight;
    }
  }, [revealed]);

  const [answer, setAnswer] = useState("");
  const won = response.startsWith("Correct");
  const wrong = response.startsWith("Incorrect");

  // Re-trigger the shake animation on each new wrong answer.
  const [shakeKey, setShakeKey] = useState(0);
  const last = useRef(response);
  useEffect(() => {
    if (response !== last.current) {
      if (response.startsWith("Incorrect")) setShakeKey((k) => k + 1);
      last.current = response;
    }
  }, [response]);

  return (
    <div className="tc-overlay" style={{ ["--acc" as string]: accent }}>
      <style>{TC_CSS}</style>
      <div className="tc-scan" />
      <div className={`tc-card ${wrong ? "tc-shake" : ""}`} key={shakeKey}>
        <button className="tc-close" onClick={onClose} aria-label="Disconnect">
          ✕
        </button>
        <div className="tc-head">
          <span className="tc-h-left">▣ {data.header || "TERMINAL"}</span>
          <span className="tc-h-right">{data.tag || ""}</span>
        </div>

        {data.art && <pre className="tc-art">{data.art}</pre>}

        <div className="tc-feed" ref={feedRef}>
          {lines.slice(0, revealed).map((l, i) => (
            <div className="tc-line" key={i}>
              <span className="tc-pre">›</span> {l}
            </div>
          ))}
          {revealed < lines.length && <span className="tc-cursor">▌</span>}
        </div>

        {won ? (
          <div className="tc-win">
            {response.replace(/^Correct answer\.\s*/, "")}
            <div className="tc-foot">
              <button className="tc-btn" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="tc-prompt">{data.prompt || "ENTER:"}</div>
            <div className="tc-row">
              <input
                className="tc-input"
                autoFocus
                value={answer}
                placeholder={data.placeholder || ""}
                onChange={(e) => setAnswer(e.target.value)}
                onKeyDownCapture={(e) => {
                  e.stopPropagation();
                  if (e.key === "Enter" && answer.trim()) onSubmit(answer.trim());
                }}
              />
              <button
                className="tc-btn tc-go"
                onClick={() => answer.trim() && onSubmit(answer.trim())}
              >
                RUN
              </button>
            </div>
            {response && (
              <div className={`tc-resp ${wrong ? "tc-bad" : ""}`}>{response}</div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const TC_CSS = `
.tc-overlay{position:fixed;inset:0;z-index:2000;display:flex;align-items:center;
  justify-content:center;background:radial-gradient(circle at 50% 38%,
  rgba(6,12,18,0.93),rgba(2,4,6,0.97));
  font-family:'MelGame','Courier New',monospace;overflow:hidden;}
.tc-scan{position:absolute;inset:0;pointer-events:none;opacity:0.16;
  background:repeating-linear-gradient(to bottom,rgba(255,255,255,0.10) 0 1px,
  transparent 1px 3px);animation:tc-roll 8s linear infinite;}
@keyframes tc-roll{to{background-position:0 120px;}}
.tc-card{position:relative;width:min(900px,92vw);height:min(660px,88vh);
  display:flex;flex-direction:column;overflow:hidden;
  background:linear-gradient(180deg,#05090d,#03060a);
  border:1px solid var(--acc);border-radius:9px;padding:20px 24px 18px;
  color:#dfe9ef;box-shadow:0 0 38px color-mix(in srgb,var(--acc) 45%,transparent),
  0 16px 54px rgba(0,0,0,0.72);}
.tc-close{position:absolute;top:9px;right:12px;background:none;border:none;
  color:#7c8c97;font-size:22px;cursor:pointer;}
.tc-head{display:flex;justify-content:space-between;align-items:baseline;
  border-bottom:1px solid color-mix(in srgb,var(--acc) 40%,transparent);
  padding-bottom:8px;margin-bottom:12px;}
.tc-h-left{color:var(--acc);letter-spacing:0.14em;font-size:22px;
  text-shadow:0 0 10px color-mix(in srgb,var(--acc) 60%,transparent);}
.tc-h-right{color:#7c8c97;font-size:16px;letter-spacing:0.12em;}
.tc-art{flex:none;color:var(--acc);font-size:17px;line-height:1.15;margin:2px 0 10px;
  white-space:pre;overflow-x:auto;max-height:22vh;overflow-y:auto;
  text-shadow:0 0 8px color-mix(in srgb,var(--acc) 50%,transparent);}
.tc-feed{flex:1 1 auto;min-height:0;overflow-y:auto;
  margin:8px 0 14px;font-size:22px;line-height:1.5;color:#cdd8df;}
.tc-line{padding:3px 0;}
.tc-pre{color:var(--acc);}
.tc-cursor{color:var(--acc);animation:tc-blink 1s steps(2) infinite;}
@keyframes tc-blink{50%{opacity:0;}}
.tc-prompt{margin:8px 0 9px;letter-spacing:0.14em;font-size:20px;color:var(--acc);}
.tc-row{display:flex;gap:10px;}
.tc-input{flex:1;background:#020607;border:1px solid #243842;color:#e6f0f5;
  padding:11px 13px;border-radius:5px;font-family:inherit;font-size:22px;outline:none;}
.tc-input:focus{border-color:var(--acc);
  box-shadow:0 0 0 2px color-mix(in srgb,var(--acc) 30%,transparent);}
.tc-btn{background:#0a1c26;border:1px solid var(--acc);color:var(--acc);
  padding:11px 18px;border-radius:5px;cursor:pointer;font-family:inherit;
  font-size:20px;letter-spacing:0.1em;}
.tc-btn:hover{background:#103040;}
.tc-go{font-weight:700;}
.tc-resp{margin-top:12px;font-size:19px;font-style:italic;color:#9fb4c0;}
.tc-bad{color:#ff8a8a;font-style:normal;}
.tc-win{flex:1 1 auto;min-height:0;overflow-y:auto;
  margin-top:12px;white-space:pre-wrap;font-size:22px;line-height:1.5;
  color:var(--acc);background:color-mix(in srgb,var(--acc) 10%,transparent);
  border:1px solid var(--acc);border-radius:6px;padding:15px;}
.tc-foot{text-align:right;margin-top:10px;}
.tc-shake{animation:tc-shake 0.4s linear;}
@keyframes tc-shake{0%,100%{transform:translateX(0);}20%{transform:translateX(-6px) skewX(-3deg);}
  40%{transform:translateX(6px);}60%{transform:translateX(-4px) skewX(2deg);}80%{transform:translateX(4px);}}
`;

export default TerminalChallenge;
